import React from 'react';
import { Box, Typography, Container, Grid, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import './References.css';

import baustelle1 from '../assets/CAD_Pictures/real_baustelle/1.jpg';
import baustelle3 from '../assets/CAD_Pictures/real_baustelle/3.jpg';
import heroCADBg from '../assets/CAD_Pictures/real_baustelle/11.jpg';

const servicesData = [
  {
    id: 1,
    title: 'Stahlbau- und Metallbauplanung',
    description:
      'Unsere Stahlbau- und Metallbauplanung richtet sich an Produktionsbetriebe, Metallbauer, Generalunternehmer und Architekten, die klare, montagefertige und normgerechte Pläne benötigen.',
    bullets: [
      'Massaufnahme und Bestandesaufnahme vor Ort',
      'Konzept-, Freigabe- und Ausführungsplanung in 2D und 3D',
      'Detaillierte Fertigungszeichnungen diverser Einzelteile (PDF, DWG, DXF, NC, STEP, IFC, 3D DWG)',
      'Werkstatt-, Schweiss- und Produktionspläne',
      'Montagepläne mit Positions- und Massangaben',
      'Stücklisten, Schraubenlisten und Bauteilkennzeichnungen',
      'Unterstützung während Produktion und Montage'
    ],
    advantage: 'Reduzierte Rückfragen, saubere Fertigung und eine effiziente Montage – dank klar strukturierter CAD-Pläne.',
    image: baustelle1
  },
  {
    id: 2,
    title: 'Rohrleitungsplanung',
    description:
      'Wir erstellen eine professionelle Rohrleitungsplanung für Industrie- und Anlagenbau – abgestimmt auf Architektur, Technik und Betrieb.',
    bullets: [
      'Massaufnahme und Bestandesaufnahme vor Ort',
      'Konzept-, Freigabe- und Ausführungsplanung in 2D und 3D',
      'Rohrleitungs-Isometrien oder 2D-Detailzeichnungen für die Produktion',
      'Montagepläne mit Positions- und Massangaben',
      'Auflistung und Kennzeichnung von Armaturen, Fittings und Messinstrumenten',
      'Einzeichnung und Vermassung aller Kleinanschlüsse',
      'Rohrpositions- und Stücklisten'
    ],
    advantage: 'Kollisionsfreie Planung, kurze Montagezeiten und hohe Betriebssicherheit.',
    image: baustelle3
  }
];

const CADServicesPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <Box className='hero-section-references' style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${heroCADBg})` }}>
        <Box className='hero-overlay-references'>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Typography
              variant='h1'
              sx={{
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5rem' },
                fontWeight: 700,
                letterSpacing: { xs: '2px', md: '8px' },
                mb: 2,
                textShadow: '2px 2px 12px rgba(0, 0, 0, 0.7)'
              }}>
              SERVICES
            </Typography>
            <Typography
              variant='h5'
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                fontWeight: 300,
                letterSpacing: { xs: '1px', md: '2px' },
                textShadow: '1px 1px 6px rgba(0, 0, 0, 0.7)'
              }}>
              Stahlbau, Metallbau und Rohrleitungsplanung
            </Typography>
          </motion.div>
        </Box>
      </Box>

      {/* Introduction Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#fff' }}>
        <Container maxWidth='lg'>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <Divider
              sx={{
                width: '80px',
                height: '3px',
                backgroundColor: '#1a1a1a',
                margin: '0 auto 3rem',
                border: 'none'
              }}
            />
            <Typography
              variant='h2'
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.5rem' },
                mb: 4,
                textAlign: 'center',
                color: '#1a1a1a'
              }}>
              Präzise CAD-Planung
            </Typography>
            <Typography
              variant='body1'
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.8,
                color: '#6b6b6b',
                textAlign: 'center',
                maxWidth: '800px',
                mx: 'auto',
                mb: 6
              }}>
              Wir bieten professionelle Stahlbau-, Metallbau- und Rohrleitungsplanung mit Fokus auf praxisgerechte CAD-Zeichnungen, effiziente
              Abläufe und eine reibungslose Umsetzung in der Produktion und auf der Baustelle. Als erfahrenes CAD-Planungsbüro unterstützen wir
              Unternehmen bei Kapazitätsengpässen oder übernehmen komplette Planungsleistungen im Outsourcing.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Services Detail Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#f9f9f9' }}>
        <Container maxWidth='xl'>
          <motion.div variants={containerVariants} initial='hidden' whileInView='visible' viewport={{ once: true, margin: '-100px' }}>
            <Grid container spacing={{ xs: 4, md: 6 }}>
              {servicesData.map((service, index) => {
                const isEven = index % 2 === 0;
                return (
                  <Grid item xs={12} key={service.id}>
                    <motion.div variants={itemVariants}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', md: isEven ? 'row' : 'row-reverse' },
                          gap: { xs: 3, md: 6 },
                          alignItems: 'flex-start'
                        }}>
                        {/* Image Column */}
                        <Box
                          sx={{
                            flex: { md: '0 0 45%' },
                            width: '100%',
                            position: 'relative',
                            overflow: 'hidden'
                          }}>
                          <Box
                            component='img'
                            src={service.image}
                            alt={service.title}
                            sx={{
                              width: '100%',
                              height: { xs: '280px', sm: '350px', md: '450px' },
                              objectFit: 'cover',
                              objectPosition: 'center',
                              transition: 'transform 0.4s ease',
                              '&:hover': { transform: 'scale(1.05)' }
                            }}
                          />
                        </Box>

                        {/* Content Column */}
                        <Box sx={{ flex: { md: '0 0 55%' }, px: { xs: 0, md: 3 } }}>
                          <Typography
                            variant='h4'
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: '1.5rem', md: '1.75rem' },
                              mb: 2,
                              color: '#1a1a1a'
                            }}>
                            {service.title}
                          </Typography>
                          <Typography
                            variant='body1'
                            sx={{
                              color: '#6b6b6b',
                              lineHeight: 1.8,
                              fontSize: { xs: '0.95rem', md: '1rem' },
                              mb: 3
                            }}>
                            {service.description}
                          </Typography>

                          {/* Bullet Points */}
                          <Box component='ul' sx={{ pl: 2, mb: 3 }}>
                            {service.bullets.map((bullet, i) => (
                              <Box
                                component='li'
                                key={i}
                                sx={{
                                  color: '#555',
                                  fontSize: { xs: '0.9rem', md: '0.95rem' },
                                  lineHeight: 1.8,
                                  mb: 0.5,
                                  '&::marker': { color: '#1a1a1a' }
                                }}>
                                {bullet}
                              </Box>
                            ))}
                          </Box>

                          {/* Advantage */}
                          <Box
                            sx={{
                              backgroundColor: '#f0f0f0',
                              px: 3,
                              py: 2,
                              borderLeft: '3px solid #1a1a1a'
                            }}>
                            <Typography
                              variant='body2'
                              sx={{
                                color: '#1a1a1a',
                                fontSize: '0.95rem',
                                fontWeight: 600
                              }}>
                              Ihr Vorteil:
                            </Typography>
                            <Typography
                              variant='body2'
                              sx={{
                                color: '#555',
                                fontSize: '0.9rem',
                                mt: 0.5
                              }}>
                              {service.advantage}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </motion.div>

                    {index < servicesData.length - 1 && (
                      <Divider sx={{ mt: { xs: 4, md: 6 }, borderColor: '#e0e0e0' }} />
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#1a1a1a' }}>
        <Container maxWidth='md'>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <Typography
              variant='h4'
              align='center'
              sx={{ mb: 3, fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' }, color: '#fff' }}>
              Bereit für Ihr Projekt?
            </Typography>
            <Typography
              variant='body1'
              align='center'
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.8,
                color: '#e0e0e0',
                mb: 4
              }}>
              Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch. Ob kurzfristige Unterstützung oder langfristiges Outsourcing – wir
              passen uns Ihren Bedürfnissen an.
            </Typography>
          </motion.div>
        </Container>
      </Box>
    </Layout>
  );
};

export default CADServicesPage;
